import "css/formDIEmailResponsive.css";
import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import SendIcon from "@mui/icons-material/Send";
import Textarea from "@mui/joy/Textarea";
import { TextField } from "@mui/material";
import PropTypes from "prop-types";
import { ENDPOINT } from "constants/variables";
import { helpHttp } from "service/HelpHttp";
import { setLoading } from "reducers/loadingSlice";
import { LoadingNews } from "components/utils/LoadingNews";
import { successErrorAction, warningErrorAction } from "actions/ErrorActions";
import "css/generalSettings.css";
import "css/customDialog.css";

/* ----------------------------- helpers/hooks ----------------------------- */

const initialStateEMAIL = {
  id: "",
  defaultStore_id: 0,
  useraccount_id: 0,
  store_EmailTo: "",
  subjectEmail: "",
  contentEmail: "",
  filetosend: [],
};

const initialEmailToSelect = [{ id: "", email: "" }];

function useDebouncedCallback(fn, delay = 300) {
  const fnRef = useRef(fn);
  const timerRef = useRef(null);

  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  const debounced = useCallback(
    (...args) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => fnRef.current(...args), delay);
    },
    [delay]
  );

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return debounced;
}

function useObjectUrl(blob) {
  const [url, setUrl] = useState("");

  useEffect(() => {
    if (!blob) {
      setUrl("");
      return;
    }
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [blob]);

  return url;
}

function base64ToPdfBlob(base64) {
  if (!base64) return null;
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: "application/pdf" });
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = () => {
      const result = reader.result || "";
      const parts = String(result).split(",");
      resolve(parts[1] || "");
    };
    reader.onerror = reject;
  });
}

/* -------------------------------- component ------------------------------ */

const FormDISendEmailList = ({ getData, showModalFormDISendEmailList, typeOfList }) => {
  const { t, i18n } = useTranslation(["listDataForms"]);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const token = useSelector((state) => state.loginUser.token);
  const defaultStore = useSelector((state) => state.loginUser.defaultStore);
  const useraccount_id = useSelector((state) => state.loginUser.useraccount_id);
  const footeremail = useSelector((state) => state.formatDIUser.footeremail);
  const isLoadingData = useSelector((state) => state.loading.isLoading);

  const [formEMAIL, setFormEMAIL] = useState(initialStateEMAIL);
  const [errorsEMAIL, setErrorsEMAIL] = useState({});
  const [emailToSelect, setEmailToSelect] = useState(initialEmailToSelect);
  const [filteredEmail, setFilteredEmail] = useState([]);
  const [highlightedIndexEmail, setHighlightedIndexEmail] = useState(-1);
  const dropdownRef = useRef(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  const pdfUrl = useObjectUrl(pdfBlob);

  /* ------------------------------ utilities ------------------------------ */

  const goBack = useCallback(() => {
    navigate(typeOfList === "1" ? "/formdi" : "/formdiended");
  }, [navigate, typeOfList]);

  const resetState = useCallback(() => {
    dispatch(setLoading(false));
    setFormEMAIL(initialStateEMAIL);
    setErrorsEMAIL({});
    setEmailToSelect(initialEmailToSelect);
    setFilteredEmail([]);
    setHighlightedIndexEmail(-1);
    setPdfBlob(null);
  }, [dispatch]);

  const resetAndClose = useCallback(() => {
    resetState();
    goBack();
  }, [resetState, goBack]);

  /* --------------------------- validation --------------------------- */

  const validateFormEmail = useCallback(() => {
    const errors = {};
    const regexInvalidCharacters = /[\\!\\$\\%\\&\\|\\<\\>\\?\\{\\}\\~\\"\\']+/;
   
    if (!formEMAIL.store_EmailTo || formEMAIL.store_EmailTo === "") {
      errors.store_EmailTo = "Text_FieldSendEmailToRequired";
    } else if (regexInvalidCharacters.test(formEMAIL.store_EmailTo)) {
      errors.store_EmailTo = "Text_TextNoValid";
    }

    if (regexInvalidCharacters.test(formEMAIL.subjectEmail || "")) {
      errors.subjectEmail = "Text_TextNoValid";
    }

    if (regexInvalidCharacters.test(formEMAIL.contentEmail || "")) {
      errors.contentEmail = "Text_TextNoValid";
    }

    return errors;
  }, [formEMAIL.store_EmailTo, formEMAIL.subjectEmail, formEMAIL.contentEmail]);

  /* ---------------------- close on Escape ---------------------- */

  useEffect(() => {
    if (!showModalFormDISendEmailList) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") resetAndClose();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [showModalFormDISendEmailList, resetAndClose]);

  /* ---------------------- fetch emails once on open ---------------------- */

  useEffect(() => {
    if (!showModalFormDISendEmailList) return;

    let alive = true;

    (async () => {
      try {
        // según tu lógica: "-1" significa traer lista completa
        const res = await helpHttp().get2(ENDPOINT, "api/getlistemails/", token, "-1");
        if (!alive) return;

        const list = Array.isArray(res) ? res.filter((x) => x?.email) : [];
        setEmailToSelect(list.length ? list : initialEmailToSelect);
      } catch {
        if (!alive) return;
        dispatch(warningErrorAction(t("Text_IncorrectData")));
      }
    })();

    return () => {
      alive = false;
    };
  }, [showModalFormDISendEmailList, token, dispatch, t]);

  /* ---------------------- init form & pdf when getData arrives ---------------------- */

  useEffect(() => {
    if (!showModalFormDISendEmailList) return;
    if (!getData) return;

    // email body
    const footeremailUpdated =
      footeremail && footeremail.trim() !== ""
        ? footeremail.replace(
            "Adjunto le remito la documentación indicada.",
            `Adjunto le remito la documentación indicada:\n ${getData?.numbersDI || ""}`
          )
        : `Adjunto le remito la documentación indicada:\n ${getData?.numbersDI || ""}`;

    setFormEMAIL((prev) => ({
      ...prev,
      defaultStore_id: defaultStore?.id || 0,
      useraccount_id: useraccount_id || 0,
      store_EmailTo: String(getData?.emails || "").toLowerCase(),
      subjectEmail: "ENVÍO DE FORMULARIOS D.I. UNIFICADOS",
      contentEmail: footeremailUpdated,
    }));

    // PDF base64 -> Blob
    if (getData?.mergedPdf) {
      setPdfBlob(base64ToPdfBlob(getData.mergedPdf));
    } else {
      setPdfBlob(null);
    }
  }, [showModalFormDISendEmailList, getData, defaultStore?.id, useraccount_id, footeremail]);

  /* ---------------------- autocomplete (local filter + debounce) ---------------------- */

  const doFilterEmails = useCallback(
    (searchValue) => {
      const q = (searchValue || "").toLowerCase().trim();
      if (!q) {
        setFilteredEmail([]);
        setHighlightedIndexEmail(-1);
        return;
      }

      const filtered = (emailToSelect || [])
        .filter((x) => x?.email)
        .filter((x) => String(x.email).toLowerCase().includes(q))
        .slice(0, 20);

      setFilteredEmail(filtered);
      setHighlightedIndexEmail(filtered.length ? 0 : -1);
    },
    [emailToSelect]
  );

  const debouncedFilterEmails = useDebouncedCallback(doFilterEmails, 250);

  const handleSelectEmail = useCallback((emailObj) => {
    const email = (emailObj?.email || "").toLowerCase();
    if (!email) return;
    setFormEMAIL((prev) => ({ ...prev, store_EmailTo: email }));
    setFilteredEmail([]);
    setHighlightedIndexEmail(-1);
  }, []);

  const scrollToElement = useCallback((index) => {
    const dropdown = dropdownRef.current;
    const item = dropdown?.children?.[index];
    item?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, []);

  const handleKeyDownEmail = useCallback(
    (event) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();

        if (filteredEmail.length === 0 && (formEMAIL.store_EmailTo || "").trim() === "") {
          const all = (emailToSelect || []).slice(0, 20);
          setFilteredEmail(all);
          setHighlightedIndexEmail(all.length ? 0 : -1);
          return;
        }

        setHighlightedIndexEmail((prev) => {
          const next = Math.min(prev + 1, filteredEmail.length - 1);
          scrollToElement(next);
          return next;
        });
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlightedIndexEmail((prev) => {
          const next = Math.max(prev - 1, 0);
          scrollToElement(next);
          return next;
        });
      } else if ((event.key === "Enter" || event.key === "Tab") && highlightedIndexEmail >= 0) {
        if (highlightedIndexEmail < filteredEmail.length) {
          event.preventDefault();
          handleSelectEmail(filteredEmail[highlightedIndexEmail]);
        }
      } else if (event.key === "Escape") {
        setFilteredEmail([]);
        setHighlightedIndexEmail(-1);
      }
    },
    [
      emailToSelect,
      filteredEmail,
      formEMAIL.store_EmailTo,
      highlightedIndexEmail,
      handleSelectEmail,
      scrollToElement,
    ]
  );

  const handleBlurCloseDropdown = useCallback((event) => {
    const next = event?.relatedTarget;
    if (next?.closest?.(".email-suggest-dropdown")) return;
    setFilteredEmail([]);
    setHighlightedIndexEmail(-1);
  }, []);

  /* ---------------------- change / blur ---------------------- */

  const handleChangeDataEditEmail = useCallback((e) => {
    const { name, value } = e.target;
    setFormEMAIL((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleBlurDataEditEmail = useCallback(
    (e) => {
      handleChangeDataEditEmail(e);
      setErrorsEMAIL(validateFormEmail());
    },
    [handleChangeDataEditEmail, validateFormEmail]
  );

  /* ---------------------- submit ---------------------- */

  const handleConfirmFetchSendFormDIList = useCallback(
    async (payload) => {
      resetAndClose();

      try {
        const res = await helpHttp().postemailformdilist(
          ENDPOINT,
          "api/sendemailwithattachmentformdilist",
          token,
          payload
        );

        if (res?.status === "OK") {
          dispatch(setLoading(false));
          dispatch(successErrorAction(t("Text_EMAIL_Sended")));
        } else {
          dispatch(setLoading(false));
          dispatch(warningErrorAction(t(res?.cause || "Text_IncorrectData")));
        }
      } catch {
        dispatch(setLoading(false));
        dispatch(warningErrorAction(t("Text_IncorrectData")));
      }
    },
    [resetAndClose, token, dispatch, t]
  );

  const handleSubmitSendEmail = useCallback(async () => {
    const errors = validateFormEmail();
    setErrorsEMAIL(errors);

    const hasNoErrors = Object.keys(errors).length === 0;
    const emailToOk = (formEMAIL?.store_EmailTo || "").trim();
    const hasPdf = !!pdfBlob;

    if (!hasNoErrors || !emailToOk || !hasPdf) {
      if (errors.store_EmailTo) dispatch(warningErrorAction(t(errors.store_EmailTo)));
      else if (errors.subjectEmail) dispatch(warningErrorAction(t(errors.subjectEmail)));
      else if (errors.contentEmail) dispatch(warningErrorAction(t(errors.contentEmail)));
      else dispatch(warningErrorAction(t("Text_IncorrectData")));
      return;
    }

    dispatch(setLoading(true));

    try {
      const base64File = await blobToBase64(pdfBlob);

      const payload = {
        id: "0",
        defaultStore_id: defaultStore?.id || 0,
        useraccount_id: useraccount_id || 0,
        store_EmailTo: formEMAIL.store_EmailTo,
        subjectEmail: formEMAIL.subjectEmail,
        contentEmail: formEMAIL.contentEmail,
        filetosend: base64File,
        fileName: "FORMULARIOS_UNIFICADOS.pdf",
        fileType: "application/pdf",
      };

      await handleConfirmFetchSendFormDIList(payload);
    } catch {
      dispatch(setLoading(false));
      dispatch(warningErrorAction(t("Text_IncorrectData")));
    }
  }, [
    validateFormEmail,
    formEMAIL,
    pdfBlob,
    dispatch,
    t,
    defaultStore?.id,
    useraccount_id,
    handleConfirmFetchSendFormDIList,
  ]);

  /* ---------------------- memo: pdf viewer ---------------------- */

  const PdfViewer = useMemo(() => {
    if (!pdfUrl) return null;
    return (
      <iframe
        src={pdfUrl}
        title="PDF Preview"
        style={{ width: "100%", height: "100%", border: "none" }}
      />
    );
  }, [pdfUrl]);

  if (!showModalFormDISendEmailList) return null;

  return (
    <div className="container-fluid email-send-wrapper formdi-email-form">
      <Box sx={{ bgcolor: "#ffffff", width: "100%" }}>
        <form onSubmit={(e) => e.preventDefault()}>
          {/* HEADER */}
          <div className="email-card-header">
            <div className="email-header-content">
              <div className="email-header-icon">
                <i className="fas fa-envelope"></i>
              </div>

              <div className="email-header-text">
                <div className="email-header-title">{t("Text_NumberForm_EMAIL")}:</div>
                <div className="email-header-number">{t("TextUNIFIED")}</div>
              </div>

              <div className="email-card-actions">
                {!isLoadingData && (
                  <>
                    <button type="button" className="email-send-btn" onClick={handleSubmitSendEmail}>
                      {t("Button_SEND_ESIR")}
                      <SendIcon sx={{ fontSize: 22, color: "white" }} />
                    </button>

                    <button type="button" className="email-cancel-btn" onClick={resetAndClose}>
                      {t("Button_Cancel")}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {!isLoadingData ? (
            <>
              {/* FORM CARD */}
              <div className="email-card-form">
                <div className="row g-3">
                  {/* Email To */}
                  <div className="col-12 email-input" style={{ position: "relative" }}>
                    <label className="text-primary" style={{ fontSize: 18 }}>
                      {t("Text_SENDEMAIL_EmailsTo")} :
                    </label>

                    <TextField
                      id="store_EmailTo"
                      name="store_EmailTo"
                      value={formEMAIL.store_EmailTo || ""}
                      onChange={(e) => {
                        const v = (e.target.value || "").toLowerCase();
                        setFormEMAIL((prev) => ({ ...prev, store_EmailTo: v }));
                        debouncedFilterEmails(v);
                      }}
                      onKeyDown={handleKeyDownEmail}
                      onBlur={handleBlurCloseDropdown}
                      label={
                        errorsEMAIL.store_EmailTo != null
                          ? t(errorsEMAIL.store_EmailTo)
                          : t("PlaceHolder_SENDEMAIL_EmailsTo")
                      }
                      placeholder={t("PlaceHolder_SENDEMAIL_EmailsTo")}
                      variant="standard"
                      inputProps={{
                        style: { width: i18n.language === "es" ? 738 : 867 },
                        maxLength: 100,
                      }}
                      InputLabelProps={{ style: { fontSize: 18, background: "#FFFFFF" } }}
                    />

                    {filteredEmail.length > 0 && (
                      <div
                        ref={dropdownRef}
                        className="email-suggest-dropdown"
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        {filteredEmail.map((opt, index) => (
                          <MenuItem
                            key={`${opt.id ?? "x"}-${opt.email}-${index}`}
                            tabIndex={0}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleSelectEmail(opt)}
                            style={{
                              cursor: "pointer",
                              padding: "10px 12px",
                              borderBottom: "1px solid #eee",
                              fontSize: 18,
                              backgroundColor: index === highlightedIndexEmail ? "#4e73df" : "transparent",
                              color: index === highlightedIndexEmail ? "white" : "black",
                            }}
                          >
                            {opt.email}
                          </MenuItem>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Subject */}
                  <div className="col-12 email-input">
                    <label className="text-primary" style={{ fontSize: 18 }}>
                      {t("Text_SENDEMAIL_Subject")}
                    </label>

                    <TextField
                      id="subjectEmail"
                      name="subjectEmail"
                      value={formEMAIL.subjectEmail ?? ""}
                      inputProps={{
                        style: { width: i18n.language === "es" ? 941 : 937 },
                        maxLength: 100,
                      }}
                      InputLabelProps={{ style: { fontSize: 18, background: "#FFFFFF" } }}
                      label={
                        errorsEMAIL.subjectEmail != null
                          ? t(errorsEMAIL.subjectEmail)
                          : t("PlaceHolder_SENDEMAIL_Subject")
                      }
                      placeholder={t("PlaceHolder_SENDEMAIL_Subject")}
                      variant="standard"
                      onBlur={handleBlurDataEditEmail}
                      onChange={handleChangeDataEditEmail}
                    />
                  </div>

                  {/* Content */}
                  <div className="col-12">
                    <label className="text-primary" style={{ fontSize: 18 }}>
                      {t("Text_SENDEMAIL_Content")} :
                    </label>

                    <Textarea
                      id="contentEmail"
                      name="contentEmail"
                      value={formEMAIL.contentEmail || ""}
                      className="email-textarea"
                      label={
                        errorsEMAIL.contentEmail != null
                          ? t(errorsEMAIL.contentEmail)
                          : t("PlaceHolder_SENDEMAIL_Content")
                      }
                      placeholder={t("PlaceHolder_SENDEMAIL_Content")}
                      sx={{ width: "100%", minWidth: 0, fontSize: 16 }}
                      variant="standard"
                      onBlur={handleBlurDataEditEmail}
                      onChange={handleChangeDataEditEmail}
                      maxRows={8}
                    />
                  </div>
                </div>
              </div>

              {/* PDF CARD */}
              <div className="email-card-pdf">{PdfViewer}</div>
            </>
          ) : (
            <LoadingNews />
          )}
        </form>
      </Box>
      <br />
    </div>
  );
};

FormDISendEmailList.propTypes = {
  showModalFormDISendEmailList: PropTypes.bool.isRequired,
  getData: PropTypes.any,
  typeOfList: PropTypes.string,
};

export default FormDISendEmailList;